<template>
    <MainContent title="Campaña de UPSELLING" subtitle="Detalles y estadísticas de la campaña">
        <ContentOrLoading>
            <div v-if="!loadingDetalle" class="tw:min-h-screen tw:pt-0 tw:flex tw:flex-col tw:gap-6">
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-6 tw:w-full">
                    <StatsCard
                        title="Total Enviados"
                        :value="campaignDetails.estadisticas.total_enviados ?? 0"
                        suffix="mensajes"
                        variant="success"
                    />
                    <StatsCard 
                        title="Tasa de apertura" 
                        :value="`${campaignDetails.estadisticas.tasa_apertura ?? 0}%`"
                        iconColor="text-success"
                        icon="fas fa-chart-line"
                        variant="success"
                    />
                    <StatsCard 
                        title="Coste acumulado" 
                        :value="(campaignDetails.estadisticas.coste_acumulado ?? 0) | formatear_precio"
                        variant="success"
                    />
                </div>
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-6">
                    <Card customClasses="tw:col-span-1 tw:md:col-span-2 tw:p-5" title="ENVÍOS POR DÍA (ÚLTIMOS 7 DÍAS)">
                        <Chart 
                            type="ColumnChart"
                            :data="chartData"
                            :options="chartOptions"
                            :height="esMovil ? 300 : 385" 
                        />
                    </Card>
                    <Card customClasses="tw:col-span-1 tw:p-5" title="Resumen de configuración">
                        <div class="tw:flex tw:flex-col tw:gap-5">
                            <div>
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Nombre</p>
                                <p class="tw:text-sm tw:font-semibold tw:text-gray-800 m-0">{{ campaignDetails.nombre }}</p>
                            </div>

                            <div>
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Tipo de campaña</p>
                                <p class="tw:text-sm tw:font-semibold tw:text-gray-800 m-0">{{ campaignDetails.tipo_campana }}</p>
                            </div>

                            <div>
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Canales habilitados</p>
                                <div class="tw:flex tw:gap-3 tw:mt-1">
                                    <ChannelInfoCard
                                        v-if="campaignDetails.canales && campaignDetails.canales.includes('whatsapp')"
                                        variant="success"
                                        label="WhatsApp"
                                        :value="campaignDetails.whatsapp_template?.nombre || 'Plantilla activa'"
                                        icon="fab fa-whatsapp"
                                    />
                                    <ChannelInfoCard
                                        v-if="campaignDetails.canales && campaignDetails.canales.includes('email')"
                                        variant="primary"
                                        label="Email"
                                        value="Campaña mailing"
                                        icon="fas fa-envelope"
                                    />
                                </div>
                            </div>

                            <div v-if="campaignDetails.canales && campaignDetails.canales.includes('email')">
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Asunto Email</p>
                                <p class="tw:text-sm tw:font-semibold tw:text-gray-800 m-0">{{ campaignDetails.asunto_email || '-' }}</p>
                            </div>

                            <div v-if="campaignDetails.canales && campaignDetails.canales.includes('email')">
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Contenido Email</p>
                                <div class="tw:max-h-40 tw:overflow-y-auto tw:border tw:border-gray-100 tw:rounded tw:p-3 tw:bg-gray-50 tw:text-sm tw:text-gray-600" v-html="sanitizedContenidoHtml"></div>
                            </div>

                            <div v-if="campaignDetails.canales && campaignDetails.canales.includes('whatsapp')">
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide  mb-1">Plantilla WhatsApp</p>
                                <p class="tw:text-sm tw:font-semibold tw:text-gray-800 m-0">{{ campaignDetails.whatsapp_template?.nombre || '-' }}</p>
                            </div>

                            <div>
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Segmento objetivo</p>
                                <div class="tw:flex tw:flex-wrap tw:gap-2">
                                    <template v-if="campaignDetails.tipos_abonos.length">
                                        <span
                                            v-for="abono in campaignDetails.tipos_abonos"
                                            :key="abono.id"
                                            class="tw:text-xs tw:font-semibold tw:border tw:border-gray-300 tw:rounded tw:px-2 tw:py-1"
                                        >
                                            {{ abono.descripcion }}
                                        </span>
                                    </template>
                                    <template v-if="campaignDetails.eventos.length">
                                        <span
                                            v-for="evento in campaignDetails.eventos"
                                            :key="evento.id"
                                            class="tw:text-xs tw:font-semibold tw:border tw:border-blue-300 tw:text-blue-600 tw:rounded tw:px-2 tw:py-1"
                                        >
                                            <i class="fas fa-calendar-week mr-1"></i>{{ evento.name }}
                                        </span>
                                    </template>
                                    <template v-if="campaignDetails.equipos.length">
                                        <span
                                            v-for="equipo in campaignDetails.equipos"
                                            :key="equipo.id"
                                            class="tw:text-xs tw:font-semibold tw:border tw:border-purple-300 tw:text-purple-600 tw:rounded tw:px-2 tw:py-1"
                                        >
                                            <i class="fas fa-shield-alt mr-1"></i>{{ equipo.nombre }}
                                        </span>
                                    </template>
                                    <span v-if="!campaignDetails.tipos_abonos.length && !campaignDetails.eventos.length && !campaignDetails.equipos.length" class="tw:text-sm tw:text-gray-400">-</span>
                                </div>
                            </div>

                            <div>
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Retraso</p>
                                <p class="tw:text-sm tw:font-semibold tw:text-gray-800 m-0">{{ campaignDetails.retraso_envio }} min.</p>
                            </div>

                            <div>
                                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wide mb-1">Estado</p>
                                <span v-if="campaignDetails.activa" class="tw:text-xs tw:font-semibold tw:bg-green-100 tw:text-green-700 tw:rounded-full tw:px-3 tw:py-1">
                                    <i class="fas fa-circle mr-1" style="font-size: 8px"></i> Activa
                                </span>
                                <span v-else class="tw:text-xs tw:font-semibold tw:bg-gray-100 tw:text-gray-500 tw:rounded-full tw:px-3 tw:py-1">
                                    <i class="fas fa-circle mr-1" style="font-size: 8px"></i> Inactiva
                                </span>
                            </div>
                        </div>
                    </Card>
                </div>
                <SimpleTable
                    title="Últimos envíos"
                    :columns="columns"
                    :data="campaignDetails.estadisticas.ultimos_envios"
                    :default-col-width="defaultColWidth"
                >
                    <template #cell-canal="{ row }">
                        <span class="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
                            <span v-if="row.enviado && row.enviado_email" class="tw:flex tw:items-center tw:gap-1">
                                <i class="fab fa-whatsapp tw:text-green-500" title="WhatsApp"></i>
                                <i class="fas fa-envelope tw:text-blue-500" title="Email"></i>
                                <span>Ambos</span>
                            </span>
                            <span v-else-if="row.enviado" class="tw:flex tw:items-center tw:gap-1">
                                <i class="fab fa-whatsapp tw:text-green-500" title="WhatsApp"></i>
                                <span>WhatsApp</span>
                            </span>
                            <span v-else-if="row.enviado_email" class="tw:flex tw:items-center tw:gap-1">
                                <i class="fas fa-envelope tw:text-blue-500" title="Email"></i>
                                <span>Email</span>
                            </span>
                            <span v-if="!row.enviado && !row.enviado_email" class="tw:text-xs tw:text-gray-400">-</span>
                        </span>
                    </template>
                    <template #cell-destinatario="{ row }">
                        <span class="tw:flex tw:items-center tw:gap-2">
                            <template v-if="row.telefono">
                                <span class="tw:text-gray-700">{{ isTelefonoVisible(row) ? row.telefono : maskTelefono(row.telefono) }}</span>
                                <button
                                    type="button"
                                    class="tw:text-gray-400 hover:tw:text-gray-600"
                                    :aria-label="isTelefonoVisible(row) ? 'Ocultar teléfono' : 'Mostrar teléfono'"
                                    @click="toggleTelefono(row)"
                                >
                                    <i :class="isTelefonoVisible(row) ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                                </button>
                            </template>
                            <span v-else>{{ row.email || row.user?.email || 'N/A' }}</span>
                        </span>
                    </template>
                    <template #cell-estado="{ value }">
                        <Badge v-bind="{ ...fallback, label: value, ...estadoConfig[value] }" />
                    </template>
                    <template #cell-coste_envio_whatsapp="{ value }">
                        <span>{{ value | formatear_precio }}</span>
                    </template>
                </SimpleTable>
            </div>
        </ContentOrLoading>
    </MainContent>
</template>

<script setup>
    import ChannelInfoCard from '@/modules/admin/ui/ChannelInfoCard.vue'
    import StatsCard from '@/modules/admin/ui/StatsCard.vue';
    import MainContent from "@/modules/admin/ui/MainContent.vue";
    import { onMounted, ref, computed } from 'vue';
    import DOMPurify from 'dompurify';
    import Chart from '@/modules/admin/ui/Chart.vue';
    import SimpleTable from '@/modules/admin/ui/table/SimpleTable.vue';
    import { useVersionMovil } from '@/composables/common/useVersionMovil';
    import Button from '@/modules/admin/ui/Button.vue';
    import useCampaign from '@/composables/admin/upselling/useCampaign';
    import Badge from '@/modules/admin/ui/Badge.vue';
    import Card from '@/modules/admin/ui/Card.vue';

    const props = defineProps({
        campaign_id: {
            type: [String, Number],
            required: true
        },
        defaultColWidth: {
            type: String,
            default: '140px'
        }
    });

    const telefonoVisible = ref({});
    const { esMovil } = useVersionMovil();
    const { campaignDetails, loadingDetalle, ultimos7, getCampaignDetails } = useCampaign(props.campaign_id);

    const ESTADO_CONFIG = {
        pendiente:  { variant: 'warning', icon: 'fas fa-clock',          label: 'Pendiente'  },
        enviado:    { variant: 'primary', icon: 'fas fa-paper-plane',    label: 'Enviado'    },
        entregado:  { variant: 'info',    icon: 'fas fa-check',          label: 'Entregado'  },
        leido:      { variant: 'success', icon: 'fas fa-check-circle',   label: 'Leído'      },
        abierto:    { variant: 'success', icon: 'fas fa-envelope-open',  label: 'Abierto'    },
        clickado:   { variant: 'success', icon: 'fas fa-mouse-pointer',  label: 'Clickado'   },
        aceptado:   { variant: 'purple',  icon: 'fas fa-star',           label: 'Aceptado'   },
        fallido:    { variant: 'danger',  icon: 'fas fa-exclamation-triangle', label: 'Fallido' },
        rechazado:  { variant: 'danger',  icon: 'fas fa-ban',            label: 'Rechazado'  },
        spam:       { variant: 'danger',  icon: 'fas fa-shield-alt',     label: 'Spam'       },
        no_enviado: { variant: 'default', icon: 'fas fa-minus-circle',  label: 'No enviado' },
    }
    const estadoConfig = ESTADO_CONFIG
    const fallback = { variant: 'default', icon: 'fas fa-question-circle' }

    const getTelefonoKey = row => row.id ?? row.user?.id ?? row.email ?? row.telefono;

    const isTelefonoVisible = row => !!telefonoVisible.value[getTelefonoKey(row)];

    const maskTelefono = telefono => telefono.replace(/\d(?=\d{2})/g, '•');
    const chartData = computed(() => [
        ['Fecha', 'Envíos'],
        ...ultimos7.value.map(e => {
            const [, month, day] = e.fecha.split('-');
            return [`${day}/${month}`, e.total];
        })
    ]);

    const sanitizedContenidoHtml = computed(
        () => DOMPurify.sanitize(campaignDetails.value?.contenido_html ?? '')
    );

    const chartOptions = computed(() => ({
        colors: ['#3b82f6'],
        bar: { groupWidth: '10%' },
        hAxis: { textStyle: { fontSize: 12, color: '#6b7280' }, gridlines: { color: 'transparent' } },
        vAxis: { minValue: 0, textStyle: { fontSize: 12, color: '#6b7280' }, format: '#' }
    }));

    const toggleTelefono = row => {
        const id = getTelefonoKey(row);

        telefonoVisible.value = {
            ...telefonoVisible.value,
            [id]: !telefonoVisible.value[id]
        };
    };

    const columns= [
        { key: 'canal', label: 'Canal' },
        { key: 'destinatario', label: 'Destinatario' },
        { key: 'user.nombre', label: 'Usuario' },
        { key: 'estado', label: 'Estado' },
        { key: 'coste_envio_whatsapp', label: 'Coste operativo' },
    ]

    onMounted(() => {
        getCampaignDetails();
    })
</script>
